import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getAdminUserStats } from "../../../utils/Api";

interface DataPoint {
  date: string;
  new_users: number;
  total_users: number;
}

interface StatsResponse {
  range: string;
  interval: string;
  data: DataPoint[];
  total_users: number;
}

const TIME_RANGES = [
  { value: "1D", label: "1D" },
  { value: "1W", label: "1W" },
  { value: "1M", label: "1M" },
  { value: "3M", label: "3M" },
  { value: "6M", label: "6M" },
  { value: "1Y", label: "1Y" },
  { value: "2Y", label: "2Y" },
  { value: "5Y", label: "5Y" },
  { value: "ALL", label: "All" },
];

const formatDate = (dateStr: string, interval: string): string => {
  const date = new Date(dateStr);
  switch (interval) {
    case "hour":
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    case "day":
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    case "week":
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    case "month":
      return date.toLocaleDateString([], { month: "short", year: "2-digit" });
    default:
      return date.toLocaleDateString();
  }
};

const UserGrowthChart: React.FC = () => {
  const [selectedRange, setSelectedRange] = useState("1M");
  const [data, setData] = useState<DataPoint[]>([]);
  const [dataInterval, setDataInterval] = useState("day");
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getAdminUserStats(selectedRange);
        if (!cancelled) {
          const stats: StatsResponse = response.data;
          setData(stats.data);
          setDataInterval(stats.interval);
          setTotalUsers(stats.total_users);
        }
      } catch (err) {
        if (!cancelled) {
          setError("Failed to load user statistics");
          console.error(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [selectedRange]);

  const formattedData = data.map((point) => ({
    ...point,
    formattedDate: formatDate(point.date, dataInterval),
  }));

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-5">
        <div>
          <h2 className="text-sm font-semibold tracking-tight text-gray-900">User growth</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Total users: {totalUsers.toLocaleString()}
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          {TIME_RANGES.map((range) => (
            <button
              key={range.value}
              onClick={() => setSelectedRange(range.value)}
              className={`h-7 px-2.5 text-xs font-medium rounded-md transition-colors ${
                selectedRange === range.value
                  ? "bg-[#1d365d] text-white"
                  : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-64 sm:h-72 flex items-center justify-center">
          <p className="text-sm text-gray-400">Loading...</p>
        </div>
      ) : error ? (
        <div className="h-64 sm:h-72 flex items-center justify-center">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      ) : data.length === 0 ? (
        <div className="h-64 sm:h-72 flex items-center justify-center">
          <p className="text-sm text-gray-400">No data available for this time range</p>
        </div>
      ) : (
        <div className="h-64 sm:h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={formattedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis
                dataKey="formattedDate"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={{ stroke: "#e5e7eb" }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
                formatter={(value, name) => {
                  const label = name === "total_users" ? "Total users" : "New users";
                  return [typeof value === "number" ? value.toLocaleString() : value, label];
                }}
                labelFormatter={(label) => label}
              />
              <Line
                type="monotone"
                dataKey="total_users"
                stroke="#1d365d"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: "#1d365d" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default UserGrowthChart;
